import { apiRequest } from '@lib/api';

import {
    ClientDetailResponse,
    ClientListResponse,
    ProvisionedOwner,
    type ClientDetail,
    type ClientSummary,
} from '../schemas/client';
import { targetParams, type ClientTarget } from '../schemas/target';

/**
 * The clients API, one function per route.
 *
 * Every one takes a {@link ClientTarget} and puts it on the query string. There
 * is no default and no ambient value: the server refuses a request that does
 * not say where it is aimed, and on a create that refusal is the difference
 * between a client in dev and a client in production.
 */

function query(target: ClientTarget, extra: Record<string, string> = {}): string {
    return new URLSearchParams({ ...targetParams(target), ...extra }).toString();
}

export async function listClients(
    target: ClientTarget,
    signal?: AbortSignal,
): Promise<ClientSummary[]> {
    const { clients } = await apiRequest(`/clients?${query(target)}`, ClientListResponse, {
        signal,
    });
    return clients;
}

export async function getClient(
    target: ClientTarget,
    tenantId: string,
    signal?: AbortSignal,
): Promise<ClientDetail | null> {
    const { client } = await apiRequest(
        `/clients/${encodeURIComponent(tenantId)}?${query(target)}`,
        ClientDetailResponse,
        { signal },
    );
    return client;
}

/**
 * Give an existing client its first person.
 *
 * Not a retry of the creation: that would fail on the address this very client
 * holds. It is a different operation, keyed on the tenant id.
 */
export function provisionOwner(
    target: ClientTarget,
    tenantId: string,
    owner: { username: string; email: string },
): Promise<ProvisionedOwner> {
    return apiRequest(
        `/clients/${encodeURIComponent(tenantId)}/owner?${query(target)}`,
        ProvisionedOwner,
        { method: 'POST', body: owner },
    );
}
